import {
  type RemixiconComponentType,
  RiArrowRightSLine,
  RiBox3Fill,
  RiCropLine,
  RiListUnordered,
  RiTruckLine
} from '@remixicon/react'
import { useCallback, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router'

import { Calendar } from '../ui/calendar'

import { NavUser } from './nav-user'
import { useFlows } from '@/api/flows/flows'
import type { Stage } from '@/api/stages/stages-types'
import { FLOW_CONFIG } from '@/components/common/badges/detail-flow-badge'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { routes } from '@/constants/routes'
import { useProdDate } from '@/hooks/use-query-states'
import { cn } from '@/lib/utils'

interface NavItemBase {
  key: string
  type: 'item' | 'skeleton'
}

interface NavItemData extends NavItemBase {
  type: 'item'
  name: string
  url: string
  icon: RemixiconComponentType
  disabled?: boolean
}

interface NavItemSkeleton extends NavItemBase {
  type: 'skeleton'
}

type NavItem = NavItemData | NavItemSkeleton

interface NavGroup {
  name: string | null
  children: NavItem[]
}

const getFlowIcon = (inven: string | undefined): RemixiconComponentType =>
  (inven ? FLOW_CONFIG[inven as keyof typeof FLOW_CONFIG]?.icon : undefined) ?? RiCropLine

const STATIC_NAV_GROUPS: NavGroup[] = [
  {
    name: '',
    children: [
      {
        key: 'scheduling-orders',
        type: 'item',
        name: 'Scheduling',
        url: routes.scheduling,
        icon: RiListUnordered,
        disabled: false
      }
    ]
  }
]

const PackaingGroup: NavGroup = {
  name: '',
  children: [
    {
      key: 'packaging',
      type: 'item',
      name: 'Packaging',
      url: routes.packaging,
      icon: RiBox3Fill,
      disabled: false
    },
    {
      key: 'deliveries',
      type: 'item',
      name: 'Deliveries',
      url: routes.deliveries,
      icon: RiTruckLine,
      disabled: false
    }
  ]
}

export const AppSidebar = () => {
  const { state, toggleSidebar } = useSidebar()

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1400 && state === 'expanded') {
        toggleSidebar()
      }
      if (window.innerWidth > 1400 && state === 'collapsed') {
        toggleSidebar()
      }
    }

    handleResize()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [state, toggleSidebar])

  return (
    <Sidebar
      className='border-grey-500 relative z-50 w-[var(--app-sidebar-width)] max-[1400px]:mr-4'
      collapsible='icon'
    >
      {/* <AppSidebarHeader isCollapsed={state === 'collapsed'} /> */}
      <AppSidebarContent isCollapsed={state === 'collapsed'} />
      <AppSidebarFooter isCollapsed={state === 'collapsed'} />
    </Sidebar>
  )
}

// const AppSidebarHeader = ({ isCollapsed }: { isCollapsed: boolean }) => {
//   return (
//     <SidebarHeader
//       className={cn(
//         'bg-grey-200 shadow-grey-500 flex h-15.5 flex-row items-center justify-between border-b shadow-[inset_-0.5px_0_0_0]',
//         isCollapsed && 'w-[var(--app-sidebar-width)] justify-center'
//       )}
//     >
//       <Link to={routes.scheduling}>
//         <Logo collapsed={isCollapsed} />
//       </Link>
//       {isCollapsed ? null : <ColorsPopup />}
//     </SidebarHeader>
//   )
// }
export const AppSidebarContent = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const { pathname } = useLocation()
  const { date, setDate } = useProdDate()

  const handleSelect = useCallback(
    (newDate: Date | undefined) => {
      if (newDate) {
        setDate(newDate)
      }
    },
    [setDate]
  )

  const { data: flows, isLoading } = useFlows()

  const skeletonDynamicItems: NavItemSkeleton[] = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => ({
        key: `skeleton-${i}`,
        type: 'skeleton'
      })),
    []
  )

  const dynamicMachineItems: NavItem[] = useMemo(() => {
    if (isLoading) return skeletonDynamicItems
    return (
      flows?.results?.flatMap<NavItemData>((flow) =>
        flow.stages.map((stage: Stage) => ({
          key: `machine-${stage.id}`,
          type: 'item',
          name: stage.name,
          url: `/machines/${stage.id}`,
          icon: getFlowIcon(flow.inven),
          disabled: false
        }))
      ) ?? []
    )
  }, [isLoading, skeletonDynamicItems, flows?.results])

  const machineNavGroup: NavGroup = useMemo(
    () => ({
      name: 'Machines',
      children: dynamicMachineItems
    }),
    [dynamicMachineItems]
  )

  const navGroups = useMemo(() => [...STATIC_NAV_GROUPS, machineNavGroup, PackaingGroup], [machineNavGroup])

  return (
    <SidebarContent
      className={cn(
        'bg-grey-200 shadow-grey-500 gap-0 px-px shadow-[inset_-0.5px_0_0_0]',
        isCollapsed && 'w-[var(--app-sidebar-width)]'
      )}
    >
      {navGroups.map((group, groupIndex) => (
        <SidebarGroup
          className='pb-0'
          key={groupIndex}
        >
          {group.name && !isCollapsed && <SidebarGroupLabel>{group.name}</SidebarGroupLabel>}
          <SidebarMenu className={cn(isCollapsed && 'items-center')}>
            {group.children.map((item) =>
              item.type === 'skeleton' ? (
                <SidebarMenuItem key={item.key}>
                  <div className='p-2'>
                    <Skeleton className='h-6 w-full' />
                  </div>
                </SidebarMenuItem>
              ) : (
                <SidebarMenuItem
                  key={item.key}
                  className={cn(group.name === 'Machines' && !isCollapsed ? 'pl-3' : '')}
                >
                  <SidebarMenuButton
                    tooltip={isCollapsed ? item.name : undefined}
                    isActive={pathname === item.url}
                    disabled={item.disabled}
                    className={cn(
                      'group peer w-full',
                      item.disabled ? 'text-grey-500 pointer-events-none' : ''
                    )}
                    asChild
                  >
                    <Link
                      className={cn(item.disabled ? 'pointer-events-none' : '')}
                      to={item.url}
                    >
                      <item.icon className='group-hover:text-black-200 group-data-[active=false]:text-grey-500 transition-colors' />
                      {!isCollapsed && (
                        <>
                          <span className='truncate'>{item.name}</span>
                          <RiArrowRightSLine className='ml-auto opacity-100 transition-opacity group-data-[active=false]:opacity-0' />
                        </>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            )}
          </SidebarMenu>
        </SidebarGroup>
      ))}
      {!isCollapsed && (
        <Calendar
          mode='single'
          selected={date}
          onSelect={handleSelect}
          month={date}
          onMonthChange={handleSelect}
          // Deliveries run on production weekends and holidays too.
          withDisabledDays={pathname.replace(/\/$/, '') !== routes.deliveries}
          className='mt-4 border-t'
        />
      )}
    </SidebarContent>
  )
}

const AppSidebarFooter = ({ isCollapsed }: { isCollapsed: boolean }) => {
  return (
    <SidebarFooter
      className={cn(
        'bg-grey-200 shadow-grey-500 flex flex-row items-center gap-1 shadow-[inset_-0.5px_0_0_0]',
        isCollapsed && 'w-[var(--app-sidebar-width)] flex-col gap-2.5'
      )}
    >
      <NavUser />
    </SidebarFooter>
  )
}
